import { FormsModule } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, input, signal } from '@angular/core';

import { TranslateModule } from '@ngx-translate/core';

import moment from 'moment';

import { IDropdownItem } from '@cakeiteasy/web-utils';
import { CakeItEasyWebUiModule, DeliveryCalendar } from '@cakeiteasy/web-ui';

import { ReturnPackagingStore } from '../../services/return-packaging.store';
import { ReturnCalendarService } from '../../services/return-calendar.service';

/**Drop-off at the seller's return outlet. Dates and times come from that outlet's calendar, and no
 * price is quoted: the outlet calendar prices pickup orders, which has nothing to do with a return.*/
@Component({
  standalone: true,
  selector: 'self-return-schedule',
  imports: [FormsModule, TranslateModule, CakeItEasyWebUiModule],
  templateUrl: './self-return-schedule.component.html',
  styleUrls: ['./self-return-schedule.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelfReturnScheduleComponent implements OnInit {
  readonly outletId = input.required<number>();
  readonly earliestDate = input<Date>(null);
  readonly heading = input<string>('');
  readonly instructions = input<string>('');

  readonly store = inject(ReturnPackagingStore);

  private readonly _calendar = inject(ReturnCalendarService);
  private readonly _sanitizer = inject(DomSanitizer);

  readonly calendar = signal<DeliveryCalendar>(null);
  readonly isCalendarLoading = signal(false);
  readonly isCalendarFailed = signal(false);

  readonly safeInstructions = computed(() =>
    this._sanitizer.bypassSecurityTrustHtml(this.instructions() || ''));

  readonly timeOptions = computed<IDropdownItem[]>(() => {
    const calendar = this.calendar();
    const date = this.store.date();

    if (!calendar || !date) {
      return [];
    }

    return this._calendar
      .getSelfReturnSlots(calendar.getDate(date))
      .map(({ timeRange }) => ({ id: timeRange, label: timeRange }));
  });

  public ngOnInit(): void {
    this.store.outletId.set(this.outletId());
    void this._loadCalendar(this._initialMonth());
  }

  public async onMonthNavigate(monthDate: string): Promise<void> {
    await this._loadCalendar(monthDate, this.calendar());
  }

  public onDateChange(date: string): void {
    this.store.date.set(date);
    this.store.time.set(null);
  }

  public onTimeChange(item: IDropdownItem): void {
    this.store.time.set(item ? String(item.id) : null);
  }

  /**Opens on the already chosen return date when the customer comes back to edit, otherwise on the
   * first date they are allowed to pick.*/
  private _initialMonth(): string {
    return this.store.date() || moment(this.earliestDate() || new Date()).format('YYYY-MM-DD');
  }

  private async _loadCalendar(monthDate: string, prevCalendar?: DeliveryCalendar): Promise<void> {
    this.isCalendarLoading.set(true);
    this.isCalendarFailed.set(false);

    try {
      this.calendar.set(await this._calendar.loadSelfReturnCalendar({
        outletId: this.outletId(),
        monthDate,
        prevCalendar,
      }));
    } catch {
      /**Without this the datepicker just shows every day as unavailable, which reads as "the outlet
       * never takes returns" rather than "we could not load the times".*/
      this.isCalendarFailed.set(true);
    } finally {
      this.isCalendarLoading.set(false);
    }
  }
}
